import React, { useState } from "react";
import "@/App.css";
import { ClaraProvider } from "@/contexts/ClaraContext";
import { AccessibilityProvider } from "@/contexts/AccessibilityContext";
import { DataProvider } from "@/contexts/DataContext";
import VoiceToast from "@/components/VoiceToast";
import AppShell from "@/components/AppShell";
import InactivityCheck from "@/components/InactivityCheck";
import HomeScreen from "@/screens/HomeScreen";
import HealthVitals from "@/screens/HealthVitals";
import Telehealth from "@/screens/Telehealth";
import VoiceShopping from "@/screens/VoiceShopping";
import Transportation from "@/screens/Transportation";
import PayBills from "@/screens/PayBills";
import Messages from "@/screens/Messages";
import CaregiverShare from "@/screens/CaregiverShare";
import { useClara } from "@/contexts/ClaraContext";
import { HELP_TEXT } from "@/data/sampleData";

const SCREEN_META = {
    home: { title: null, eyebrow: null, showBack: false, helpText: HELP_TEXT.home },
    vitals: {
        title: "Health Vitals",
        eyebrow: "Your readings",
        showBack: true,
        helpText: HELP_TEXT.vitals,
    },
    telehealth: {
        title: "Doctor Visits",
        eyebrow: "Telehealth",
        showBack: true,
        helpText: HELP_TEXT.telehealth,
    },
    shopping: {
        title: "Voice Shopping",
        eyebrow: "Shop anywhere",
        showBack: true,
        helpText: HELP_TEXT.shopping,
    },
    transport: {
        title: "Food & Rides",
        eyebrow: "Delivery & rideshare",
        showBack: true,
        helpText: HELP_TEXT.transport,
    },
    bills: {
        title: "Pay Bills",
        eyebrow: "Secure voice payments",
        showBack: true,
        helpText: HELP_TEXT.bills,
    },
    messages: {
        title: "Messages",
        eyebrow: "Family & care team",
        showBack: true,
        helpText: HELP_TEXT.messages,
    },
    caregiver: {
        title: "Weekly summary",
        eyebrow: "For your caregivers",
        showBack: true,
        helpText: HELP_TEXT.caregiver,
    },
};

function AppInner() {
    const [screen, setScreen] = useState("home");
    const [inactivityOpen, setInactivityOpen] = useState(false);
    const { speak, stop } = useClara();

    const navigate = (next) => {
        setScreen(next);
    };

    const goHome = () => {
        stop();
        speak("Back to home.");
        setScreen("home");
    };

    const meta = SCREEN_META[screen];

    let content = null;
    switch (screen) {
        case "vitals":
            content = <HealthVitals />;
            break;
        case "telehealth":
            content = <Telehealth onBackHome={goHome} />;
            break;
        case "shopping":
            content = <VoiceShopping />;
            break;
        case "transport":
            content = <Transportation />;
            break;
        case "bills":
            content = <PayBills />;
            break;
        case "messages":
            content = <Messages />;
            break;
        case "caregiver":
            content = <CaregiverShare />;
            break;
        case "home":
        default:
            content = <HomeScreen onNavigate={navigate} />;
    }

    return (
        <>
            <AppShell
                title={meta.title}
                eyebrow={meta.eyebrow}
                showBack={meta.showBack}
                onBack={goHome}
                helpText={meta.helpText}
                onSimulateInactivity={() => setInactivityOpen(true)}
            >
                {content}
            </AppShell>
            <InactivityCheck
                open={inactivityOpen}
                onDismiss={() => setInactivityOpen(false)}
            />
        </>
    );
}

function App() {
    return (
        <div className="App">
            <AccessibilityProvider>
                <DataProvider>
                    <ClaraProvider>
                        <AppInner />
                        <VoiceToast />
                    </ClaraProvider>
                </DataProvider>
            </AccessibilityProvider>
        </div>
    );
}

export default App;
